import React, { useState, useEffect } from 'react';
import { Database, Power, Clock, Settings } from 'lucide-react';
import { connectionManager } from '../lib/supabase/connectionManager';

interface ConnectionControlProps {
  isAdmin?: boolean;
}

export function ConnectionControl({ isAdmin }: ConnectionControlProps) {
  const [isConnected, setIsConnected] = useState(connectionManager.isConnected());
  const [showSettings, setShowSettings] = useState(false);
  const [duration, setDuration] = useState<string>('60');
  const [autoDisconnect, setAutoDisconnect] = useState(false);

  // Subscribe to connection changes
  useEffect(() => {
    return connectionManager.subscribe(setIsConnected);
  }, []);

  useEffect(() => {
    const state = connectionManager.getConnectionState();
    setAutoDisconnect(state.autoDisconnect);
    if (state.connectionDuration) {
      setDuration(state.connectionDuration.toString());
    }
  }, []);

  const handleConnect = async () => {
    const success = await connectionManager.connect();
    setIsConnected(success);
    
    if (success && autoDisconnect) {
      connectionManager.setAutoDisconnect(parseInt(duration));
    }
  };

  const handleDisconnect = () => {
    connectionManager.disconnect();
    setIsConnected(false);
  };

  const handleSaveSettings = () => {
    if (autoDisconnect) {
      connectionManager.setAutoDisconnect(parseInt(duration));
    } else {
      connectionManager.setAutoDisconnect(null);
    }
    setShowSettings(false);
  };

  return (
    <div className="relative">
      <div className="flex items-center gap-1 sm:gap-4">
        {isAdmin && (
          <button
            onClick={isConnected ? handleDisconnect : handleConnect}
            className={`flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-1.5 text-xs sm:text-sm rounded-md text-white transition-colors ${
              isConnected ? 'bg-red-600 hover:bg-red-700' : 'bg-green-600 hover:bg-green-700'
            }`}
          >
            <Power className="w-4 h-4" />
            <span className="hidden sm:inline">{isConnected ? 'Disconnect' : 'Connect'}</span>
            <span className="sm:hidden">{isConnected ? 'DC' : 'CN'}</span>
          </button>
        )}

        {isAdmin && (
          <button
            onClick={() => setShowSettings(!showSettings)}
            className="p-1.5 text-primary-300 hover:text-white transition-colors"
          >
            <Settings className="w-5 h-5" />
          </button>
        )}

      </div>

      {showSettings && (
        <div className="absolute top-full mt-2 right-0 bg-white rounded-lg shadow-lg p-4 w-72 sm:w-80 z-50">
          <h3 className="text-sm font-medium text-gray-900 mb-4 flex items-center gap-2">
            <Database className="w-4 h-4" />
            Connection Settings
          </h3>

          <div className="space-y-4">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={autoDisconnect}
                onChange={(e) => setAutoDisconnect(e.target.checked)}
                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <span className="text-sm text-gray-700">Auto-disconnect</span>
            </label>

            {autoDisconnect && (
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-gray-400" />
                <select
                  value={duration}
                  onChange={(e) => setDuration(e.target.value)}
                  className="rounded-md border-gray-300 text-sm focus:border-primary-500 focus:ring-primary-500"
                >
                  <option value="30">30 minutes</option>
                  <option value="60">1 hour</option>
                  <option value="120">2 hours</option>
                  <option value="240">4 hours</option>
                  <option value="480">8 hours</option>
                </select>
              </div>
            )}

            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowSettings(false)}
                className="px-3 py-1.5 text-gray-600 hover:text-gray-800"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveSettings}
                className="px-3 py-1.5 bg-primary-600 text-white rounded-md hover:bg-primary-700"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}